import { useState, useCallback } from 'react';
import { Tool } from '../types/Shape';

export interface ArrowDefaults {
  startArrow: boolean;
  endArrow: boolean;
  isCurved: boolean;
  stroke: string;
  strokeWidth: number;
}

export interface ShapeDefaults {
  fill: string;
  stroke: string;
  strokeWidth: number;
  fontSize: number;
}

export interface UseCanvasToolsReturn {
  // Tool state
  currentTool: Tool;
  setCurrentTool: React.Dispatch<React.SetStateAction<Tool>>;

  // Arrow defaults
  arrowDefaults: ArrowDefaults;
  setDefaultStartArrow: React.Dispatch<React.SetStateAction<boolean>>;
  setDefaultEndArrow: React.Dispatch<React.SetStateAction<boolean>>;
  setDefaultIsCurved: React.Dispatch<React.SetStateAction<boolean>>;
  setDefaultArrowStroke: React.Dispatch<React.SetStateAction<string>>;
  setDefaultArrowStrokeWidth: React.Dispatch<React.SetStateAction<number>>;

  // Shape defaults
  shapeDefaults: ShapeDefaults;
  setDefaultFill: React.Dispatch<React.SetStateAction<string>>;
  setDefaultStroke: React.Dispatch<React.SetStateAction<string>>;
  setDefaultStrokeWidth: React.Dispatch<React.SetStateAction<number>>;
  setDefaultFontSize: React.Dispatch<React.SetStateAction<number>>;

  // Connection points visibility
  showConnectionPoints: boolean;
  setShowConnectionPoints: React.Dispatch<React.SetStateAction<boolean>>;

  // Tool change handler
  handleToolChange: (tool: Tool, onToolChange?: () => void) => void;
}

export function useCanvasTools(): UseCanvasToolsReturn {
  // Tool state
  const [currentTool, setCurrentTool] = useState<Tool>('select');

  // Arrow defaults
  const [defaultStartArrow, setDefaultStartArrow] = useState(false);
  const [defaultEndArrow, setDefaultEndArrow] = useState(true);
  const [defaultIsCurved, setDefaultIsCurved] = useState(false);
  const [defaultArrowStroke, setDefaultArrowStroke] = useState('#000000');
  const [defaultArrowStrokeWidth, setDefaultArrowStrokeWidth] = useState(2);

  // Shape defaults
  const [defaultFill, setDefaultFill] = useState('#FFFFFF');
  const [defaultStroke, setDefaultStroke] = useState('#D1D5DB');
  const [defaultStrokeWidth, setDefaultStrokeWidth] = useState(1);
  const [defaultFontSize, setDefaultFontSize] = useState(16);

  // Connection points visibility
  const [showConnectionPoints, setShowConnectionPoints] = useState(false);

  // Tool change handler
  const handleToolChange = useCallback((tool: Tool, onToolChange?: () => void) => {
    setCurrentTool(tool);
    if (tool === 'arrow') {
      setShowConnectionPoints(true);
    } else {
      setShowConnectionPoints(false);
    }
    // 외부 콜백 호출 (예: setShowResizeHandles(false))
    onToolChange?.();
  }, []);

  return {
    // Tool state
    currentTool,
    setCurrentTool,

    // Arrow defaults (grouped for convenience)
    arrowDefaults: {
      startArrow: defaultStartArrow,
      endArrow: defaultEndArrow,
      isCurved: defaultIsCurved,
      stroke: defaultArrowStroke,
      strokeWidth: defaultArrowStrokeWidth,
    },
    setDefaultStartArrow,
    setDefaultEndArrow,
    setDefaultIsCurved,
    setDefaultArrowStroke,
    setDefaultArrowStrokeWidth,

    // Shape defaults (grouped for convenience)
    shapeDefaults: {
      fill: defaultFill,
      stroke: defaultStroke,
      strokeWidth: defaultStrokeWidth,
      fontSize: defaultFontSize,
    },
    setDefaultFill,
    setDefaultStroke,
    setDefaultStrokeWidth,
    setDefaultFontSize,

    // Connection points
    showConnectionPoints,
    setShowConnectionPoints,

    // Handler
    handleToolChange,
  };
}
